import React, { useMemo, useState } from "react";
import { Envelope, GeoAlt, Telephone, GearFill, XLg } from "react-bootstrap-icons";
import { useNavigate, useParams } from "react-router-dom";

import Counsellorroom from "../assets/CounsellorConcept.png";
import CounsellorProfileFemale from "../assets/CounsellorProfileFemale.png";
import CounsellorProfileMale from "../assets/CounsellorProfileMale.png";

import balance from "../assets/heros/balance.png";
import beach from "../assets/heros/beach.png";
import calmradiance from "../assets/heros/calmradiance.png";
import forrest from "../assets/heros/forrest.png";
import landscape from "../assets/heros/landscape.png";
import quietroom from "../assets/heros/quietroom.png";
import river from "../assets/heros/river.png";
import room from "../assets/heros/room.png";
import window from "../assets/heros/window.png";

export default function CounsellingConcept() {
  const { id } = useParams();
  const navi = useNavigate();

  const profileImage =
    id === "1" ? CounsellorProfileFemale : CounsellorProfileMale;

  const palettes = useMemo(
    () => [
      {
        id: "warm",
        name: "Warm neutral",
        vars: {
          "--bg": "#F7F3EE",
          "--surface": "#FFFFFF",
          "--section": "#F3EADF",
          "--text": "#1F2B2A",
          "--muted": "#5F6D6A",
          "--brand": "#6B7C75",
          "--brandHover": "#5E6E68",
          "--border": "#E4DAD0",
          "--footer": "#1E2322",
        },
      },
      {
        id: "sage",
        name: "Sage and cream",
        vars: {
          "--bg": "#F6F8F6",
          "--surface": "#FFFFFF",
          "--section": "#EEF3EF",
          "--text": "#1F2B2A",
          "--muted": "#5E6B65",
          "--brand": "#4F6F63",
          "--brandHover": "#436056",
          "--border": "#DDE6E1",
          "--footer": "#1B2321",
        },
      },
      {
        id: "bluegrey",
        name: "Soft blue grey",
        vars: {
          "--bg": "#F5F7FA",
          "--surface": "#FFFFFF",
          "--section": "#EEF2F6",
          "--text": "#1E2A33",
          "--muted": "#576574",
          "--brand": "#4C6475",
          "--brandHover": "#415868",
          "--border": "#DCE3EA",
          "--footer": "#1B232B",
        },
      },
      {
        id: "rose",
        name: "Dusty rose",
        vars: {
          "--bg": "#FAF6F7",
          "--surface": "#FFFFFF",
          "--section": "#F4EAEC",
          "--text": "#2B1F24",
          "--muted": "#6D5B62",
          "--brand": "#7A5A66",
          "--brandHover": "#6B4E59",
          "--border": "#E7D7DD",
          "--footer": "#241A1F",
        },
      },
      {
        id: "deepgreen",
        name: "Deep calm green",
        vars: {
          "--bg": "#F6F7F6",
          "--surface": "#FFFFFF",
          "--section": "#EDEFEA",
          "--text": "#172423",
          "--muted": "#556360",
          "--brand": "#2F5D50",
          "--brandHover": "#284F45",
          "--border": "#D9DED7",
          "--footer": "#0F1E1B",
        },
      },
      {
        id: "mono",
        name: "Minimal warm",
        vars: {
          "--bg": "#F7F7F5",
          "--surface": "#FFFFFF",
          "--section": "#F0EEE9",
          "--text": "#1D1D1B",
          "--muted": "#5F5F59",
          "--brand": "#3E3E3A",
          "--brandHover": "#2F2F2C",
          "--border": "#E3E1DA",
          "--footer": "#1A1A18",
        },
      },
    ],
    []
  );

  const pages = useMemo(
    () => [
      { id: "home", label: "Home" },
      { id: "about", label: "About" },
      { id: "services", label: "Services" },
      { id: "approach", label: "Approach" },
      { id: "pricing", label: "Pricing" },
      { id: "contact", label: "Contact" },
    ],
    []
  );

  const [paletteId, setPaletteId] = useState("warm");
  const activePalette = palettes.find((p) => p.id === paletteId) || palettes[0];
  const [activePage, setActivePage] = useState("home");

  const [heroBg, setHeroBg] = useState("balance");

  const [websiteType, setWebsiteType] = useState("multi-page");

  const [switcherOpen, setSwitcherOpen] = useState(false);

  const handleWebsiteTypeChange = (value) => {
    setWebsiteType(value);

    if (value === "one-page") {
      navi(`/counselling-concept/${id}`);
    }

    if (value === "multi-page") {
      navi(`/counselling-concept-two/${id}`);
    }

    if (value === "hybrid") {
      navi(`/counselling-concept-three/${id}`);
    }
  };

  const css = useMemo(() => {
    const themes = palettes
      .map((p) => {
        const vars = Object.entries(p.vars)
          .map(([k, v]) => `${k}: ${v};`)
          .join("\n");
        return `.theme-${p.id}{\n${vars}\n}`;
      })
      .join("\n\n");

    return `
${themes}

.cc-root{
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: var(--bg);
  color: var(--text);
}

.cc-main{
  flex: 1 0 auto;
}

.cc-topbar{
  background: var(--surface);
  border-bottom: 1px solid var(--border);
}

.cc-nav-link{
  color: var(--text);
  opacity: 0.9;
}

.cc-nav-link:hover{
  color: var(--text);
  opacity: 1;
}

.cc-page-shell{
  padding-top: 2rem;
  padding-bottom: 2rem;
}

.cc-card{
  background: var(--surface);
  border: 0;
  border-radius: 18px;
  box-shadow: 0 10px 24px rgba(0,0,0,0.06);
}

.cc-panel{
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 18px;
}

.cc-section{
  background: var(--section);
}

.cc-pill{
  display: inline-block;
  padding: 6px 12px;
  border-radius: 999px;
  background: var(--surface);
  border: 1px solid var(--border);
  font-size: 13px;
  color: var(--muted);
}

.cc-muted{
  color: var(--muted);
}

.cc-hero-title{
  font-size: 42px;
  line-height: 1.15;
}

@media (max-width: 576px){
  .cc-hero-title{ font-size: 34px; }
}

.cc-btn-brand{
  border-radius: 12px;
  padding: 12px 26px;
  font-weight: 600;
  line-height: 1.2;
  border: 1px solid var(--brand);
  background: var(--brand);
  color: var(--surface);
  text-decoration: none;
  display: inline-block;
}

.cc-btn-brand:hover{
  background: var(--brandHover);
  border-color: var(--brandHover);
  color: var(--surface);
}

.cc-btn-outline{
  border-radius: 12px;
  padding: 12px 26px;
  font-weight: 600;
  line-height: 1.2;
  border: 1px solid var(--brand);
  background: transparent;
  color: var(--brand);
  text-decoration: none;
  display: inline-block;
}

.cc-btn-outline:hover{
  background: var(--brand);
  color: var(--surface);
}

.cc-image-wrap{
  border-radius: 18px;
  overflow: hidden;
  border: 1px solid var(--border);
  background: var(--surface);
}

.cc-image{
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover;
}

.cc-image-profile{
  min-height: 320px;
}

.cc-image-room{
  min-height: 340px;
}

/* hero background */
.cc-hero{
  background-image: var(--hero-bg);
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  padding-top: 3rem;
  padding-bottom: 3rem;
}

/* full width hero even when nested inside a Bootstrap container */
.cc-hero-full{
  width: 100vw;
  margin-left: calc(50% - 50vw);
}

.cc-hero-panel{
  background: rgba(255, 255, 255, 0.4);
  border-radius: 18px;
  border: 1px solid var(--border);
}

/* set hero background by class, no inline styles */
.cc-hero-bg-balance{ --hero-bg: url(${balance}); }
.cc-hero-bg-beach{ --hero-bg: url(${beach}); }
.cc-hero-bg-calmradiance{ --hero-bg: url(${calmradiance}); }
.cc-hero-bg-forrest{ --hero-bg: url(${forrest}); }
.cc-hero-bg-landscape{ --hero-bg: url(${landscape}); }
.cc-hero-bg-quietroom{ --hero-bg: url(${quietroom}); }
.cc-hero-bg-river{ --hero-bg: url(${river}); }
.cc-hero-bg-room{ --hero-bg: url(${room}); }
.cc-hero-bg-window{ --hero-bg: url(${window}); }

.cc-switcher-wrap{
  position: fixed;
  top: 100px;
  left: 16px;
  z-index: 1050;
}

.cc-switcher{
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 14px;
  padding: 10px 12px;
  box-shadow: 0 8px 20px rgba(0,0,0,0.08);
  width: 220px;
}

.cc-switcher-toggle{
  width: 44px;
  height: 44px;
  border-radius: 14px;
  background: var(--surface);
  border: 1px solid var(--border);
  box-shadow: 0 8px 20px rgba(0,0,0,0.08);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: var(--text);
}

.cc-switcher-header{
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}

.cc-switcher-label{
  font-size: 12px;
  font-weight: 600;
  margin-bottom: 6px;
  color: var(--muted);
}

.cc-switcher-title{
  font-size: 12px;
  font-weight: 600;
  color: var(--muted);
}

.cc-switcher-close{
  width: 32px;
  height: 32px;
  border-radius: 10px;
  background: transparent;
  border: 1px solid var(--border);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: var(--muted);
}

.cc-select{
  width: 100%;
  border-color: var(--border);
  color: var(--text);
  background-color: var(--surface);
  font-size: 14px;
}

.cc-page{
  display: none;
}

.cc-page-active{
  display: block;
}

.cc-kicker{
  max-width: 820px;
  font-size: 18px;
  line-height: 1.7;
}

.cc-price{
  color: var(--brand);
  font-size: 28px;
}

.cc-small-note{
  font-size: 13px;
}

.cc-footer{
  margin-top: auto;
  background: var(--footer);
  color: var(--surface);
}
`;
  }, [palettes]);

  const go = (nextId) => setActivePage(nextId);

  return (
    <div className={`cc-root theme-${activePalette.id} cc-hero-bg-${heroBg}`}>
      <style>{css}</style>

      <div className="cc-switcher-wrap d-none d-md-block">
        {!switcherOpen && (
          <button
            type="button"
            className="cc-switcher-toggle"
            onClick={() => setSwitcherOpen(true)}
            aria-label="Open style menu"
          >
            <GearFill size={18} />
          </button>
        )}

        {switcherOpen && (
          <div className="cc-switcher">
            <div className="cc-switcher-header">
              <div className="cc-switcher-title">Change style</div>
              <button
                type="button"
                className="cc-switcher-close"
                onClick={() => setSwitcherOpen(false)}
                aria-label="Close style menu"
              >
                <XLg size={16} />
              </button>
            </div>

            <select
              className="form-select form-select-sm cc-select"
              value={paletteId}
              onChange={(e) => setPaletteId(e.target.value)}
            >
              {palettes.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>

            <div className="mt-2">
              <div className="cc-switcher-label">Change Image</div>
              <select
                className="form-select form-select-sm cc-select"
                value={heroBg}
                onChange={(e) => setHeroBg(e.target.value)}
              >
                <option value="balance">Balance</option>
                <option value="beach">Beach</option>
                <option value="calmradiance">Calm radiance</option>
                <option value="forrest">Forrest</option>
                <option value="landscape">Landscape</option>
                <option value="quietroom">Quiet room</option>
                <option value="river">River</option>
                <option value="room">Room</option>
                <option value="window">Window</option>
              </select>
            </div>

            <div className="mt-2">
              <div className="cc-switcher-label">Website type</div>
              <select
                className="form-select form-select-sm cc-select"
                value={websiteType}
                onChange={(e) => handleWebsiteTypeChange(e.target.value)}
              >
                <option value="one-page">One page</option>
                <option value="multi-page">Multi page</option>
                <option value="hybrid">Hybrid</option>
              </select>
            </div>
          </div>
        )}
      </div>

      <nav className="navbar navbar-expand-lg cc-topbar">
        <div className="container py-2">
          <button
            type="button"
            className="btn  p-0 text-decoration-none fw-bold"
            onClick={() => go("home")}
          >
            Counselling Practice
          </button>

          <button
            className="navbar-toggler"
            type="button"
            data-bs-toggle="collapse"
            data-bs-target="#navbarNav"
          >
            <span className="navbar-toggler-icon"></span>
          </button>

          <div className="collapse navbar-collapse" id="navbarNav">
            <ul className="navbar-nav ms-auto">
              {pages.map((p) => (
                <li className="nav-item" key={p.id}>
                  <button
                    type="button"
                    className="btn btn-link nav-link cc-nav-link text-decoration-none"
                    onClick={() => go(p.id)}
                  >
                    {p.label}
                  </button>
                </li>
              ))}

              <li className="nav-item d-md-none mt-2 mt-lg-0">
                <select
                  className="form-select form-select-sm"
                  value={paletteId}
                  onChange={(e) => setPaletteId(e.target.value)}
                >
                  {palettes.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </select>

                <div className="mt-2">
                  <select
                    className="form-select form-select-sm"
                    value={heroBg}
                    onChange={(e) => setHeroBg(e.target.value)}
                  >
                    <option value="balance">Balance</option>
                    <option value="beach">Beach</option>
                    <option value="calmradiance">Calm radiance</option>
                    <option value="forrest">Forrest</option>
                    <option value="landscape">Landscape</option>
                    <option value="quietroom">Quiet room</option>
                    <option value="river">River</option>
                    <option value="room">Room</option>
                    <option value="window">Window</option>
                  </select>
                </div>

                <div className="mt-2">
                  <select
                    className="form-select form-select-sm"
                    value={websiteType}
                    onChange={(e) => handleWebsiteTypeChange(e.target.value)}
                  >
                    <option value="one-page">One page</option>
                    <option value="multi-page">Multi page</option>
                    <option value="hybrid">Hybrid</option>
                  </select>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </nav>

      <div className="cc-main">
        <div className="container cc-page-shell">
          <div className={`cc-page ${activePage === "home" ? "cc-page-active" : ""}`}>
            <section className="cc-hero cc-hero-full">
              <div className="container">
                <div className="cc-hero-panel p-4 p-md-5">
                  <div className="row g-4 align-items-center">
                    <div className="col-12 col-lg-7">
                      <div className="d-flex flex-wrap gap-2 mb-3">
                        <span className="cc-pill">Confidential</span>
                        <span className="cc-pill">Supportive</span>
                        <span className="cc-pill">Online or in person</span>
                      </div>

                      <h1 className="fw-bold mb-2 cc-hero-title">
                        A calm space to talk things through
                      </h1>

                      <p className="mb-4 cc-muted cc-kicker">
                        A counselling website concept focused on warmth, clarity,
                        and simplicity. Designed to explain what is offered and
                        make getting in touch feel easy.
                      </p>

                      <div className="d-flex flex-wrap gap-3">
                        <button
                          type="button"
                          className="cc-btn-brand"
                          onClick={() => go("contact")}
                        >
                          Make an enquiry
                        </button>
                        <button
                          type="button"
                          className="cc-btn-outline"
                          onClick={() => go("services")}
                        >
                          View services
                        </button>
                      </div>
                    </div>

                    <div className="col-12 col-lg-5">
                      <div className="cc-image-wrap">
                        <img
                          src={profileImage}
                          alt="Counsellor profile"
                          className="cc-image cc-image-profile"
                          loading="lazy"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="row g-3 mt-4">
                    <div className="col-12 col-md-4">
                      <div className="cc-panel p-3">
                        <div className="fw-bold">50 minute sessions</div>
                        <div className="cc-muted">Typical length</div>
                      </div>
                    </div>
                    <div className="col-12 col-md-4">
                      <div className="cc-panel p-3">
                        <div className="fw-bold">Flexible scheduling</div>
                        <div className="cc-muted">Weekday availability</div>
                      </div>
                    </div>
                    <div className="col-12 col-md-4">
                      <div className="cc-panel p-3">
                        <div className="fw-bold">Online sessions</div>
                        <div className="cc-muted">Secure video</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <div className="row g-3 mt-3">
              <div className="col-12 col-md-6">
                <div className="cc-panel p-4">
                  <div className="fw-bold mb-1">What you can expect</div>
                  <div className="cc-muted">
                    A clear first step, a gentle pace, and space to explore what
                    is going on.
                  </div>
                </div>
              </div>
              <div className="col-12 col-md-6">
                <div className="cc-panel p-4">
                  <div className="fw-bold mb-1">A simple next step</div>
                  <div className="cc-muted">
                    Send a short message and you will get a reply within 24 to
                    48 hours.
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className={`cc-page ${activePage === "about" ? "cc-page-active" : ""}`}>
            <div className="cc-panel p-4 cc-section">
              <h2 className="fw-bold mb-1">About</h2>
              <div className="cc-muted">
                A welcoming introduction, with quick clarity on who you are and
                how you work.
              </div>
            </div>

            <div className="row g-4 mt-3 align-items-center">
              <div className="col-12 col-lg-12">
                <div className="cc-panel p-4">
                  <p className="cc-muted mb-3">
                    This section introduces the counsellor and explains how
                    sessions tend to work, in a clear and welcoming way.
                  </p>
                  <ul className="cc-muted mb-0">
                    <li>Qualifications and memberships</li>
                    <li>Areas of focus and experience</li>
                    <li>Ongoing training and supervision</li>
                  </ul>
                </div>

                <div className="row g-3 mt-3">
                  <div className="col-12 col-md-6">
                    <div className="cc-panel p-3">
                      <div className="fw-bold">Values</div>
                      <div className="cc-muted">
                        Warmth, clarity, collaboration
                      </div>
                    </div>
                  </div>
                  <div className="col-12 col-md-6">
                    <div className="cc-panel p-3">
                      <div className="fw-bold">Format</div>
                      <div className="cc-muted">Online or in person</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className={`cc-page ${activePage === "services" ? "cc-page-active" : ""}`}>
            <div className="cc-panel p-4 cc-section">
              <h2 className="fw-bold mb-1">Services</h2>
              <div className="cc-muted">
                A simple overview of what is offered and who it may help.
              </div>
            </div>

            <div className="row g-4 mt-3 align-items-center">
              <div className="col-12 col-lg-6">
                <div className="row g-3">
                  {[
                    {
                      t: "Individual counselling",
                      d: "One to one sessions to explore what is going on and what you want to change.",
                    },
                    {
                      t: "Support with stress and worry",
                      d: "Practical tools and a calmer understanding of patterns and triggers.",
                    },
                    {
                      t: "Relationship support",
                      d: "Help with boundaries, communication, and repeating difficulties.",
                    },
                  ].map((x) => (
                    <div key={x.t} className="col-12">
                      <div className="cc-panel p-4">
                        <div className="fw-bold mb-1">{x.t}</div>
                        <div className="cc-muted">{x.d}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="col-12 col-lg-6">
                <div className="cc-image-wrap">
                  <img
                    src={Counsellorroom}
                    alt="Counselling room"
                    className="cc-image cc-image-room"
                    loading="lazy"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className={`cc-page ${activePage === "approach" ? "cc-page-active" : ""}`}>
            <div className="cc-panel p-4 cc-section">
              <h2 className="fw-bold mb-1">Approach</h2>
              <div className="cc-muted">Clear, human, and paced to you.</div>
            </div>

            <div className="cc-card p-4 mt-3">
              <div className="row g-3">
                {[
                  {
                    t: "Start gently",
                    d: "A first session to understand what is bringing you here, and what you want from counselling.",
                  },
                  {
                    t: "Work together",
                    d: "We look at patterns, feelings, and needs, and build tools that fit your day to day life.",
                  },
                  {
                    t: "Review and adjust",
                    d: "We regularly check what is helping, and change direction if something is not working.",
                  },
                ].map((x) => (
                  <div className="col-12" key={x.t}>
                    <div className="cc-panel p-4">
                      <div className="fw-bold mb-1">{x.t}</div>
                      <div className="cc-muted">{x.d}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="cc-panel p-4 mt-3">
              <div className="fw-bold mb-1">A note on privacy</div>
              <div className="cc-muted">
                Add your confidentiality details here, plus any limits you need
                to explain.
              </div>
            </div>
          </div>

          <div className={`cc-page ${activePage === "pricing" ? "cc-page-active" : ""}`}>
            <div className="cc-panel p-4 cc-section">
              <h2 className="fw-bold mb-1">Pricing</h2>
              <div className="cc-muted">Simple and easy to scan.</div>
            </div>

            <div className="row g-3 mt-3">
              <div className="col-12 col-lg-6">
                <div className="cc-card p-4">
                  <div className="cc-muted mb-2">Session fee</div>
                  <div className="fw-bold cc-price">£50 per session</div>
                  <div className="cc-muted mt-2">
                    Add a short note about length and any options you offer.
                  </div>
                  <div className="d-flex flex-wrap gap-2 mt-3">
                    <span className="cc-pill">50 minutes</span>
                    <span className="cc-pill">Online</span>
                    <span className="cc-pill">In person</span>
                  </div>
                </div>
              </div>

              <div className="col-12 col-lg-6">
                <div className="cc-panel p-4">
                  <div className="fw-bold mb-2">What is included</div>
                  <ul className="cc-muted mb-0">
                    <li>Regular sessions</li>
                    <li>Online or in person options</li>
                    <li>Review and adjust as needed</li>
                  </ul>
                </div>

                <div className="cc-panel p-4 mt-3">
                  <div className="fw-bold mb-1">Cancellation policy</div>
                  <div className="cc-muted">
                    Add your policy here in a short, clear sentence.
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className={`cc-page ${activePage === "contact" ? "cc-page-active" : ""}`}>
            <div className="cc-panel p-4 cc-section">
              <h2 className="fw-bold mb-1">Contact</h2>
              <div className="cc-muted">Make getting in touch feel easy.</div>
            </div>

            <div className="row g-3 mt-3">
              <div className="col-12 col-lg-6">
                <div className="cc-panel p-4">
                  <p className="cc-muted mb-3">
                    Add your preferred contact method and what happens next.
                  </p>

                  <div className="d-flex align-items-center mb-2">
                    <Telephone className="me-2" size={20} />
                    <span>07XXX XXXXXX</span>
                  </div>

                  <div className="d-flex align-items-center mb-2">
                    <Envelope className="me-2" size={20} />
                    <span>example@email.com</span>
                  </div>

                  <div className="d-flex align-items-center">
                    <GeoAlt className="me-2" size={20} />
                    <span>City, County</span>
                  </div>

                  <div className="cc-muted mt-3 cc-small-note">
                    Typical reply time within 24 to 48 hours
                  </div>
                </div>
              </div>

              <div className="col-12 col-lg-6">
                <div className="cc-panel p-4">
                  <div className="fw-bold mb-2">Enquiry placeholder</div>
                  <div className="cc-muted mb-3">
                    Replace with a form or booking system when ready.
                  </div>

                  <div className="d-grid gap-2">
                    <button type="button" className="cc-btn-brand">
                      Request an appointment
                    </button>
                    <button type="button" className="cc-btn-outline">
                      Ask a question
                    </button>
                  </div>

                  <div className="cc-muted mt-3 cc-small-note">
                    Typical reply time within 24 to 48 hours
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <footer className="py-5 cc-footer">
        <div className="container text-center">
          <div className="fw-bold mb-1">Counselling Practice</div>
          <div className="small">
            © {new Date().getFullYear()} Counselling Practice
          </div>
        </div>
      </footer>
    </div>
  );
}
